import { ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseLeg, CaseMoney, ExpenseLine } from '@/types/domain'

import { signedNumber, varianceTone } from './money'

/** The plan against the fact, line by line: one row per folded cost line, and
 *  the served column sums under them.
 *
 *  Takes the block rather than the expense array, because the totals row is not
 *  arithmetic over the rows — see the footer's own note.
 *
 *  ── Detail groups ──────────────────────────────────────────────────────────
 *  A planned line may name another as the generalized plan it details
 *  (`detailOf`, spec 2026-09-01-expense-detail-grouping L1–L4): the quote
 *  carries «Transportation service 8 709» and the rates actually agreed per
 *  truck detail it. The server stops counting a detailed parent's own plan the
 *  moment one detail exists, so this table must not show it as a line either —
 *  the parent becomes a collapsed group whose three figures are its details',
 *  and its quote figure moves under the name as the benchmark it now is. The
 *  grouping is composed HERE from the flat list; no group aggregate is served
 *  and none is invented, only served per-row figures added up.
 */
export function ExpenseTable({
  caseId,
  legs,
  money,
  onEdit,
  onDelete,
  pending,
}: {
  caseId: string
  /** The case's route, for the direction column. The rows carry a `legId` and
   *  a `vehicleId` (FR-67); the SEQUENCE and the PLATE those resolve to live on
   *  the legs, and this table joins the two rather than asking the server for a
   *  label it can compose here — the same division `AgedItem` keeps, where the
   *  server serves the id and the route is the client's to build. */
  legs: CaseLeg[]
  money: CaseMoney
  onEdit: (expense: ExpenseLine) => void
  /** Drops the whole line — the plan and every actual against it, which is what
   *  the row's own confirmation says out loud before it calls this. */
  onDelete: (expense: ExpenseLine) => void
  /** A money mutation is in flight; the row acts go quiet rather than queueing
   *  a second delete behind the first. */
  pending?: boolean
}) {
  const { t } = useTranslation()
  const currency = money.rate.currency
  const legById = new Map(legs.map((leg) => [leg.id, leg]))

  const byId = new Map(money.expenses.map((expense) => [expense.id, expense]))
  /* Details by parent id. A detail whose parent this payload does not carry —
     deleted in another tab, 0060's ON DELETE SET NULL not yet reached this
     cached copy — stays a top-level row rather than vanishing into a group
     nothing renders. The same forgiveness `LegLink` shows a leg the route has
     lost, and for the same reason: the two facts are composed separately. */
  const details = new Map<string, ExpenseLine[]>()
  for (const expense of money.expenses) {
    if (expense.detailOf !== undefined && byId.has(expense.detailOf)) {
      details.set(expense.detailOf, [
        ...(details.get(expense.detailOf) ?? []),
        expense,
      ])
    }
  }
  const topLevel = money.expenses.filter(
    (expense) => expense.detailOf === undefined || !byId.has(expense.detailOf),
  )

  /* Collapsed by default, and deliberately not persisted: the group exists to
     keep six truck rates out of the way of the eight lines that are not
     trucks, so every visit starts from the summary rather than from wherever
     the last reader left it. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <div className="asa-scroll mt-3 overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            <th scope="col" className="asa-eyebrow h-9 px-[18px]">{t('cases.money.columns.line')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3">{t('cases.money.columns.vendor')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3">{t('cases.money.columns.leg')}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.plannedIn', { currency })}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.actualIn', { currency })}</th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('cases.money.columns.variance')}</th>
            {/* Right-set like the acts under it, and named rather than left as
                a bare column: a heading-less trailing column reads as part of
                the variance beside it. */}
            <th scope="col" className="asa-eyebrow h-9 px-[18px] text-right">{t('cases.money.columns.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {topLevel.map((expense) => {
            const children = details.get(expense.id)
            if (children === undefined) {
              return (
                <ExpenseRow
                  key={expense.id}
                  caseId={caseId}
                  expense={expense}
                  legs={legById}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  pending={pending}
                />
              )
            }

            const open = expanded.has(expense.id)
            const group = groupFigures(expense, children)

            return (
              <ExpenseRow
                key={expense.id}
                caseId={caseId}
                expense={expense}
                legs={legById}
                onEdit={onEdit}
                onDelete={onDelete}
                pending={pending}
                figures={group}
                /* The chevron is the ONLY thing that folds. The rest of the
                   row keeps the act every other row has — it opens the parent
                   for editing — so the press has to be stopped here rather
                   than the row's own handler being weakened for the group. */
                lead={
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={t('cases.money.toggleGroup', {
                      line: expense.description,
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                      toggle(expense.id)
                    }}
                    className="text-text-muted hover:text-text-heading mr-1.5 inline-flex align-middle"
                  >
                    <ChevronRight
                      size={14}
                      className={cn('transition-transform', open && 'rotate-90')}
                    />
                  </button>
                }
                /* The quote figure and how far the contracted rates have moved
                   from it. Across currencies there is no delta to state —
                   subtracting CNY from USD would be the one piece of money
                   arithmetic this client must never do — so the note falls
                   back to the count alone. */
                subnote={
                  group.uniform
                    ? t('cases.money.groupNote', {
                        quota: formatNumber(expense.planned.amount, 2),
                        delta: signedNumber(
                          String(group.planned - Number(expense.planned.amount)),
                        ),
                        count: children.length,
                      })
                    : t('cases.money.groupNoteMixed', { count: children.length })
                }
              >
                {open
                  ? children.map((child) => (
                      <ExpenseRow
                        key={child.id}
                        caseId={caseId}
                        expense={child}
                        legs={legById}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        pending={pending}
                        indent
                      />
                    ))
                  : null}
              </ExpenseRow>
            )
          })}
        </tbody>
        {/* Column sums are SERVED (AD-9), and deliberately not the
            header's actualCost: the header forecasts the whole case,
            this row totals only what has actually been recorded. */}
        <tfoot>
          <tr className="border-line border-t-2">
            <td colSpan={3} className="text-text-muted px-3 py-3 text-right text-[13px]">
              {t('cases.money.totals')}
            </td>
            <td className="asa-num text-text-heading px-3 py-3 text-right text-[13px] font-semibold">
              {formatNumber(money.plannedCost.amount, 2)}
            </td>
            <td className="asa-num text-text-heading px-3 py-3 text-right text-[13px] font-semibold">
              {money.actualToDate ? formatNumber(money.actualToDate.amount, 2) : '—'}
            </td>
            <td
              className={cn(
                'asa-num px-3 py-3 text-right text-[13px]',
                money.costVariance
                  ? varianceTone(money.costVariance.amount)
                  : 'text-text-muted',
              )}
            >
              {money.costVariance ? signedNumber(money.costVariance.amount) : '—'}
            </td>
            {/* Nothing to act on in a totals row; the cell exists so the
                column count matches the header. */}
            <td className="px-[18px]" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** What a group shows instead of its parent's own three figures.
 *
 *  Addition over figures the server already converted and sent, which is the
 *  whole of the licence this client has (AD-9): the footer totals stay served.
 *  The parent's OWN actual is added in because a retro invoice against the
 *  generalized line is still money spent — the server counts it in
 *  ActualToDate for exactly that reason — while its plan is not, because the
 *  details replaced it.
 *
 *  `uniform` is about the DELTA, not the sums: a benchmark comparison across
 *  two currencies is a subtraction nobody can make, and the note drops it. */
function groupFigures(parent: ExpenseLine, rows: ExpenseLine[]) {
  const sum = (pick: (row: ExpenseLine) => string | undefined) =>
    rows.reduce((total, row) => total + Number(pick(row) ?? 0), 0)

  const hasActual =
    parent.actual !== undefined || rows.some((row) => row.actual !== undefined)
  const hasVariance = rows.some((row) => row.variance !== undefined)

  return {
    uniform: rows.every((row) => row.planned.currency === parent.planned.currency),
    planned: sum((row) => row.planned.amount),
    actual: hasActual
      ? sum((row) => row.actual?.amount) + Number(parent.actual?.amount ?? 0)
      : undefined,
    variance: hasVariance ? sum((row) => row.variance?.amount) : undefined,
  }
}

/** One cost line's `<tr>` — a plain row, a group's parent, or a detail inside
 *  an open group. One component for all three so the three cannot drift: the
 *  parent adds a `lead` (the chevron) and a `subnote`, a detail indents, and
 *  the group replaces the three figures with `figures`. Everything else — the
 *  edit affordance, the attention wash, the direction link — is the same row
 *  it has always been.
 *
 *  `children` are the detail rows, rendered as siblings AFTER this `<tr>`: a
 *  row cannot nest a row, and a fragment keeps them in the one `<tbody>`. */
function ExpenseRow({
  caseId,
  expense,
  legs,
  onEdit,
  onDelete,
  pending,
  lead,
  subnote,
  figures,
  indent,
  children,
}: {
  caseId: string
  expense: ExpenseLine
  legs: Map<string, CaseLeg>
  onEdit: (expense: ExpenseLine) => void
  onDelete: (expense: ExpenseLine) => void
  pending?: boolean
  lead?: ReactNode
  subnote?: ReactNode
  figures?: ReturnType<typeof groupFigures>
  indent?: boolean
  children?: ReactNode
}) {
  const { t } = useTranslation()

  /* The delete asks first, IN THE ROW, and the second press carries the word
     rather than the icon — the same shape the sheet's own DeleteRow uses, and
     for the same reason it exists at all: this drops the plan AND every actual
     recorded against it, and it now sits one press away from a pencil. An
     icon that deleted on the first press would put the dangerous act on the
     safe act's muscle memory, which is precisely what DESIGN.md § Icons and
     FormSheet's header both refuse. */
  const [confirming, setConfirming] = useState(false)

  /* A booking note and a pending pass-through can coexist;
     either way the line explains itself under its own name. */
  const note = [
    expense.note,
    expense.pendingClientApproval ? t('cases.money.passThroughPending') : null,
  ]
    .filter(Boolean)
    .join(' — ')

  /* A mixed-currency group shows NO figures at all: its sums would be the one
     piece of money arithmetic this client must never do (AD-9), and a number
     over rows in two currencies reads as an amount somebody computed. The
     expanded rows carry the honest per-line figures. */
  const planned =
    figures ? (figures.uniform ? String(figures.planned) : undefined) : expense.planned.amount
  const actual = figures
    ? figures.uniform && figures.actual !== undefined
      ? String(figures.actual)
      : undefined
    : expense.actual?.amount
  const variance = figures
    ? figures.uniform && figures.variance !== undefined
      ? String(figures.variance)
      : undefined
    : expense.variance?.amount

  return (
    <>
      <tr
        className={cn(
          'border-line-hair cursor-pointer border-b transition-colors last:border-b-0',
          /* The undecided pass-through holds its whole row on the
             attention wash — the same yellow every surface uses
             for "waiting on someone", so the table itself says
             which line the Super-admin is sitting on. */
          expense.pendingClientApproval ? 'bg-attention-soft' : 'hover:bg-surface-hover',
        )}
        onClick={() => onEdit(expense)}
        tabIndex={0}
        aria-label={t('cases.money.editExpense')}
        onKeyDown={(event) => {
          /* Only the ROW's own keys open the edit. The chevron is a real
             button inside this cell, and its Enter/Space bubble up here —
             without this guard one press would both fold the group and open
             the parent's form, and preventDefault would eat the button's
             own activation. */
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onEdit(expense)
          }
        }}
      >
        <td className={cn('py-2.5 align-top', indent ? 'pr-3 pl-9' : 'px-[18px]')}>
          {lead}
          <span className="text-text-heading text-[13px]">{expense.description}</span>
          {subnote ? (
            <span className="text-text-muted mt-0.5 block max-w-72 text-[12px] leading-snug font-light">
              {subnote}
            </span>
          ) : null}
          {note ? (
            <span
              className={cn(
                'mt-0.5 block max-w-72 text-[12px] leading-snug font-light',
                expense.pendingClientApproval ? 'text-attention' : 'text-text-muted',
              )}
            >
              {note}
            </span>
          ) : null}
        </td>
        <td className="text-text-muted px-3 py-2.5 align-top text-[12.5px]">
          <SuppressedValue
            value={expense.counterpartyName}
            label={t('common.suppressed.vendor')}
          />
        </td>
        {/* What this cost paid for — a link to the direction's own
            page, with the truck when the row names one (FR-67).
            A cost that belongs to no single movement says so in words
            rather than leaving the cell blank: a clearance fee or a
            bank charge is legitimately unattributed, and an empty cell
            would read as a row somebody forgot to file.

            A GROUP leaves it blank instead: its details sit on different
            directions and the group has no single one, which the expanded
            rows say for themselves. */}
        <td className="px-3 py-2.5 align-top text-[12.5px]">
          {figures ? null : <LegLink caseId={caseId} expense={expense} legs={legs} />}
        </td>
        <td className="asa-num text-text-body px-3 py-2.5 text-right align-top text-[13px]">
          {planned === undefined ? '—' : formatNumber(planned, 2)}
        </td>
        <td className="asa-num text-text-body px-3 py-2.5 text-right align-top text-[13px]">
          {actual === undefined ? '—' : formatNumber(actual, 2)}
        </td>
        <td
          className={cn(
            'asa-num px-[18px] py-2.5 text-right align-top text-[13px]',
            variance === undefined ? 'text-text-muted' : varianceTone(variance),
          )}
        >
          {variance === undefined ? '—' : signedNumber(variance)}
        </td>
        {/* The row's own acts. Every press stops here: the whole row opens the
            edit sheet, so a click that reached it would run two acts at once —
            and a delete confirmation dismissed by the sheet it opened would be
            unusable. */}
        <td
          className="px-[18px] py-2.5 text-right align-top whitespace-nowrap"
          onClick={(event) => event.stopPropagation()}
        >
          {confirming ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-text-body text-[12.5px] leading-snug">
                {t('cases.money.deleteExpenseConfirm')}
              </span>
              <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false)
                  onDelete(expense)
                }}
              >
                {t('cases.money.deleteExpenseConfirmAction')}
              </Button>
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5">
              <IconButton
                size="sm"
                icon={Pencil}
                disabled={pending}
                label={t('cases.money.editExpenseNamed', { line: expense.description })}
                onClick={() => onEdit(expense)}
              />
              <IconButton
                size="sm"
                icon={Trash2}
                disabled={pending}
                className="hover:text-negative"
                label={t('cases.money.deleteExpenseNamed', { line: expense.description })}
                onClick={() => setConfirming(true)}
              />
            </span>
          )}
        </td>
      </tr>
      {children}
    </>
  )
}

/** Which movement a cost paid for — «Направление 2 · 63AJN05», linked.
 *
 *  The row's own act is EDIT (the whole row is clickable), so this link stops
 *  the click from reaching it: a reader pressing a link means the link, and a
 *  sheet opening over the page they asked to navigate to is the sort of thing
 *  that teaches people not to press it again.
 *
 *  A `legId` naming a leg this payload does not carry falls back to the
 *  unattributed wording rather than linking to a page that would 404. That is
 *  not defensive noise: the money block and the route are composed from two
 *  modules, and a cost on a leg somebody deleted in another tab is exactly the
 *  state this cell can be handed. */
function LegLink({
  caseId,
  expense,
  legs,
}: {
  caseId: string
  expense: ExpenseLine
  legs: Map<string, CaseLeg>
}) {
  const { t } = useTranslation()

  const leg = expense.legId ? legs.get(expense.legId) : undefined
  if (!leg) {
    return <span className="text-text-muted font-light">{t('cases.money.unattributed')}</span>
  }

  const plate = expense.vehicleId
    ? leg.vehicles?.find((vehicle) => vehicle.id === expense.vehicleId)?.plate
    : undefined

  return (
    <Link
      to={`/cases/${caseId}/legs/${leg.id}`}
      onClick={(event) => event.stopPropagation()}
      className="text-text-link hover:text-text-link-hover"
    >
      {plate
        ? t('cases.money.legLinkVehicle', { n: leg.sequence, plate })
        : t('cases.money.legLink', { n: leg.sequence })}
    </Link>
  )
}
